import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Map, Download } from "lucide-react";
import { api } from "@/lib/api";
import { absolutizeUrl } from "@/components/ImagePicker";
import { useLang } from "@/context/LanguageContext";
import NewsletterForm from "@/components/NewsletterForm";
import SEO from "@/components/SEO";
import NextEvents from "@/components/events/NextEvents";
import WetTrailsAlert from "@/components/WetTrailsAlert";
import { trailStatus } from "@/components/trailCondition";
import ShopSpotlight from "@/components/shop/ShopSpotlight";
import { MataLogo } from "@/components/MataMark";
import { ARGENTARIO_COORDS } from "@/components/EditorialLabel";
import DecorationLayer from "@/components/DecorationLayer";
import { ABNORMAL_ASSETS } from "@/components/AbnormalIllustrations";

const HERO_IMG = "https://images.pexels.com/photos/11049373/pexels-photo-11049373.jpeg";

/* State-badge for photo overlays (REAL trail state from DB) */
const stateTone = (tr, lang) => { const st = trailStatus(tr, lang); return { dot: st.theme.dot, label: st.label.toUpperCase() }; };

/**
 * Home
 * Zero SVG doodles / decorative shapes. Only:
 *   • Photos (dominant)
 *   • Editorial typography (acid green accents, brackets, coords)
 *   • The 5 user-owned poster PNGs, placed in dedicated bands
 *     placed via DecorationLayer (admin drag & drop)
 */
export default function Home() {
    const { t, lang } = useLang();
    const [featured, setFeatured] = useState(null);
    const [trails, setTrails] = useState([]);

    useEffect(() => {
        api.get("/trails/featured").then((r) => setFeatured(r.data)).catch(() => {});
        api.get("/trails").then((r) => setTrails(r.data || [])).catch(() => {});
    }, []);

    const previewTrails = trails.slice(0, 4);

    return (
        <div data-testid="home-page" className="bg-abnormal-ink text-abnormal-paper relative">
            <SEO path="/" />
            {/* Admin-editable overlay of Abnormal posters */}
            <DecorationLayer page="home" />

            {/* ═══════════ 01 · HERO PHOTO ═══════════ */}
            <section data-testid="hero-section" className="relative overflow-hidden bg-black">
                <div className="relative min-h-[92vh]">
                    <img
                        src={HERO_IMG}
                        alt="Argentario rider"
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="eager"
                        fetchPriority="high"
                        decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/10 to-black" />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />

                    {/* Top-left brand strip — pure typography */}
                    <div className="absolute top-24 md:top-28 left-5 md:left-10 font-vt text-[10px] md:text-xs tracking-[0.3em] uppercase text-abnormal-paper/85">
                        <p className="text-abnormal-acid">{ARGENTARIO_COORDS}</p>
                        <p className="text-abnormal-paperDim/70 mt-1">MONTE ARGENTARIO · TOSCANA</p>
                        <p data-testid="hero-live-badge" className="mt-2 flex items-center gap-1.5 text-abnormal-paper/85">
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-abnormal-acid opacity-70 animate-ping" />
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-abnormal-acid" />
                            </span>
                            <span className="tracking-[0.35em]">LIVE · <span className="text-abnormal-acid">{trails.length || 14} TRAIL</span></span>
                        </p>
                    </div>

                    {/* Bottom — title + subtitle */}
                    <div className="relative z-10 h-full min-h-[92vh] flex flex-col justify-end pb-20 md:pb-16 px-5 md:px-10 max-w-[1600px] mx-auto">
                        <div className="flex items-center gap-3 label-mono text-abnormal-acid mb-4 poster-in">
                            <span className="h-px w-8 bg-abnormal-acid" />
                            <span>ISSUE N° 26</span>
                            <span className="w-1 h-1 rounded-full bg-abnormal-acid" />
                            <span className="text-abnormal-paperDim/80">FIELD DISPATCH · TOSCANA</span>
                        </div>
                        <h1
                            data-testid="hero-title"
                            className="font-display uppercase leading-[0.85] tracking-tighter text-abnormal-paper
                                       text-[64px] sm:text-[96px] md:text-[150px] lg:text-[200px] xl:text-[230px]"
                        >
                            Abnormalworld<span className="text-abnormal-acid">.</span>
                        </h1>
                        <p
                            data-testid="hero-tagline"
                            className="mt-4 flex flex-wrap items-center gap-3 font-display uppercase text-abnormal-paper text-3xl md:text-5xl leading-none tracking-tight poster-in poster-delay-1"
                        >
                            <span>Monte Argentario</span>
                            <span className="w-2 h-2 rounded-full bg-abnormal-acid inline-block" />
                            <span className="text-abnormal-paperDim">Enduro MTB</span>
                        </p>
                        <p data-testid="hero-sub" className="mt-5 max-w-xl text-abnormal-paperDim text-base md:text-lg leading-relaxed">
                            {t("home.subtitle").replace("{n}", String(trails.length || 14))}
                        </p>
                        <div className="mt-6 grid grid-cols-2 sm:flex sm:flex-wrap items-stretch gap-3 sm:gap-4 max-w-xl">
                            <Link to="/trail" data-testid="hero-cta-trails" className="btn-primary justify-center !px-5 sm:!px-8 !py-4 !text-xs sm:!text-sm">
                                {lang === "en" ? "Explore the trails" : "Scopri i trail"} <ArrowUpRight size={14} />
                            </Link>
                            <Link to="/shop" data-testid="hero-cta-shop" className="inline-flex items-center justify-center gap-2 bg-abnormal-magenta text-black hover:bg-abnormal-paper px-5 sm:px-8 py-4 text-xs sm:text-sm uppercase tracking-[0.2em] font-bold transition-colors">
                                {lang === "en" ? "Explore the shop" : "Scopri lo shop"} <ArrowUpRight size={14} />
                            </Link>
                            <Link to="/ride-call" data-testid="hero-cta-rides" className="col-span-2 sm:col-span-1 label-mono text-abnormal-paperDim hover:text-abnormal-acid inline-flex items-center justify-center sm:justify-start gap-1.5 py-2">
                                Ride Call <ArrowRight size={12} className="text-abnormal-acid" />
                            </Link>
                        </div>
                        <p data-testid="hero-two-souls" className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 label-mono text-abnormal-paperDim/80">
                            <span className="text-abnormal-acid">ABnormal Outdoor</span><span>· trail, ride, eventi</span>
                            <span className="w-1 h-1 rounded-full bg-abnormal-paperDim/50" />
                            <span className="text-abnormal-magenta">ABnormal Clothing</span><span>· grow different</span>
                        </p>
                    </div>
                </div>
            </section>

            <WetTrailsAlert className="mx-5 md:mx-10 mt-6" />

            <ShopSpotlight lang={lang} />

            <NextEvents lang={lang} />

            {/* ═══════════ 02 · SMALL DETAIL STRIP ═══════════ */}
            <section className="bg-black border-y border-white/10">
                <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-3 flex items-center justify-between gap-4 flex-wrap">
                    <p className="font-vt text-[10px] md:text-xs tracking-[0.4em] uppercase text-abnormal-paperDim/70 flex items-center gap-2">
                        <span>ISSUE N° 26</span>
                        <span className="w-1 h-1 rounded-full bg-abnormal-acid inline-block" />
                        <span>MMXXVI</span>
                        <span className="w-1 h-1 rounded-full bg-abnormal-acid inline-block" />
                        <span>FIELD DISPATCH</span>
                    </p>
                    <p className="font-vt text-[10px] md:text-xs tracking-[0.4em] uppercase text-abnormal-acid">
                        {ARGENTARIO_COORDS}
                    </p>
                </div>
            </section>

            {/* ═══════════ 03 · TRAIL PREVIEW — 4 IMMERSIVE PHOTOS ═══════════ */}
            <section data-testid="trail-preview" className="relative bg-black">
                <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12 md:py-16">
                    <div data-testid="home-mata" className="mb-8 flex items-center gap-5 md:gap-8 border border-white/15 bg-abnormal-charcoal p-4 md:p-6 max-w-3xl">
                        <MataLogo className="w-20 sm:w-24 md:w-32 h-auto flex-shrink-0" testid="home-mata-logo" />
                        <div>
                            <p className="label-mono text-abnormal-acid">[ {lang === "en" ? "The territory" : "Il territorio"} ]</p>
                            <p className="mt-1 font-display uppercase leading-[0.9] tracking-tight text-abnormal-paper text-2xl sm:text-3xl md:text-4xl">Monte Argentario <span className="text-abnormal-acid">Trail Area</span></p>
                            <p className="mt-2 text-sm md:text-base text-abnormal-paperDim leading-relaxed">
                                {lang === "en"
                                    ? "M.A.T.A. is the name given to the trail area of Monte Argentario: one promontory, one network of singletracks between sea and macchia."
                                    : "M.A.T.A. è il nome dato all'area dei trail del Monte Argentario: un promontorio, una rete di sentieri tra mare e macchia."}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-baseline justify-between border-b border-white/15 pb-3 mb-6">
                        <div className="flex items-baseline gap-3">
                            <span className="font-vt text-[11px] md:text-sm tracking-[0.35em] text-abnormal-acid">[ N° 01 ]</span>
                            <span className="w-4 h-px bg-abnormal-acid/60" />
                            <span className="font-vt text-[11px] md:text-sm tracking-[0.4em] uppercase text-abnormal-paper">
                                {lang === "en" ? "The trails" : "I trail"}
                            </span>
                        </div>
                        <Link
                            to="/trail"
                            className="group font-vt text-[10px] md:text-sm tracking-[0.3em] uppercase text-abnormal-paperDim/70 hover:text-abnormal-acid inline-flex items-center gap-1 transition-colors"
                        >
                            {lang === "en" ? "Trail report" : "Trail report"}
                            <ArrowRight size={12} className="text-abnormal-acid group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                        {previewTrails.map((tr, i) => {
                            const name = (lang === "en" && tr.name_en) ? tr.name_en : tr.name;
                            const st = stateTone(tr, lang);
                            return (
                                <Link
                                    key={tr.id}
                                    to={`/trail/${tr.slug}`}
                                    data-testid={`preview-trail-${tr.slug}`}
                                    className="group relative block overflow-hidden aspect-[16/10] bg-zinc-900"
                                >
                                    <img
                                        src={absolutizeUrl(tr.image_url)}
                                        alt={name}
                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                                    <div className="absolute top-3 left-3 flex items-center gap-2 font-vt text-[10px] tracking-[0.3em] uppercase text-abnormal-paper/85">
                                        <span className="text-abnormal-acid">N° {String(i + 1).padStart(2, "0")}</span>
                                        <span className="w-3 h-px bg-abnormal-acid/60" />
                                        <span className="opacity-70">{tr.length_km} km · {tr.elevation_m} m · {tr.difficulty}</span>
                                    </div>
                                    <div className="absolute top-9 left-3 lg:top-3 lg:left-auto lg:right-3 flex items-center gap-1.5 bg-black/60 border border-white/20 px-2 py-1 font-vt text-[9px] tracking-[0.3em] uppercase text-abnormal-paper">
                                        <span className={`w-1.5 h-1.5 rounded-full ${st.dot} animate-pulse`} />
                                        {st.label}
                                    </div>
                                    {tr.trailforks_url && (
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); window.open(tr.trailforks_url, "_blank", "noopener,noreferrer"); }}
                                            data-testid={`trailforks-${tr.slug}`}
                                            className="absolute bottom-4 right-4 flex items-center gap-1 font-vt text-[10px] tracking-[0.3em] uppercase text-abnormal-acid opacity-0 group-hover:opacity-100 transition-opacity hover:underline underline-offset-4 decoration-1 pointer-events-auto"
                                        >
                                            TRAILFORKS <ArrowUpRight size={11} />
                                        </button>
                                    )}
                                    <div className="absolute bottom-4 left-4 right-4">
                                        <h3 className="font-display uppercase text-3xl md:text-5xl leading-[0.9] tracking-tight text-abnormal-paper">
                                            <span className="bg-gradient-to-r from-abnormal-acid to-abnormal-acid bg-no-repeat bg-[length:0%_2px] bg-left-bottom group-hover:bg-[length:100%_2px] transition-[background-size] duration-500 pb-0.5">
                                                {name}
                                            </span>
                                            <span className="text-abnormal-acid">.</span>
                                        </h3>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>

                    {/* Quick map strip — every trail, direct Trailforks (map + GPX) link */}
                    {trails.length > 0 && (
                        <div data-testid="home-trail-map-strip" className="mt-8 border border-white/15 bg-abnormal-charcoal">
                            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10 flex-wrap">
                                <p className="label-mono text-abnormal-acid inline-flex items-center gap-2"><Map size={12} /> {lang === "en" ? "Maps & GPX · all trails" : "Mappe & GPX · tutti i trail"}</p>
                                <p className="w-full md:w-auto label-mono text-abnormal-paperDim/60 !text-[9px]" data-testid="home-map-gpx-note">{lang === "en" ? "GPX download on Trailforks requires a free account" : "Download GPX su Trailforks: serve un account gratuito"}</p>
                                <Link to="/trail" data-testid="home-map-strip-all" className="label-mono text-abnormal-paperDim hover:text-abnormal-acid inline-flex items-center gap-1">{lang === "en" ? "Trail index" : "Indice trail"} <ArrowRight size={11} /></Link>
                            </div>
                            <div className="flex gap-2 overflow-x-auto px-4 py-3 snap-x [scrollbar-width:thin]">
                                {trails.map((tr) => (
                                    <a key={tr.slug} href={tr.trailforks_url || tr.komoot_url || `/trail/${tr.slug}`} target={tr.trailforks_url || tr.komoot_url ? "_blank" : undefined} rel="noreferrer"
                                        data-testid={`home-map-${tr.slug}`}
                                        className="group snap-start flex-shrink-0 w-[184px] border border-white/10 hover:border-abnormal-acid bg-black px-3 py-2.5 transition-colors">
                                        <p className="font-display uppercase text-lg leading-none text-abnormal-paper group-hover:text-abnormal-acid truncate">{(lang === "en" && tr.name_en) || tr.name}</p>
                                        <p className="mt-1 label-mono text-abnormal-paperDim/70 whitespace-nowrap">{tr.length_km} km · {tr.elevation_m} m D−</p>
                                        <p className="mt-1.5 label-mono text-abnormal-acid inline-flex items-center gap-1 whitespace-nowrap"><Download size={10} /> {tr.trailforks_url ? (tr.trailforks_url.includes("/region/") ? (lang === "en" ? "Area map" : "Mappa dell'area") : (lang === "en" ? "Map · GPX" : "Mappa · GPX")) : (lang === "en" ? "Trail page" : "Scheda")}</p>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* ═══════════ 04 · SMALL DETAIL — IRONIC LINE ═══════════ */}
            <section className="bg-black border-t border-white/10">
                <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-8 md:py-10 text-center">
                    <p className="font-display uppercase text-2xl md:text-4xl tracking-tight text-abnormal-paper">
                        {lang === "en" ? "Trail closed? " : "Sentiero chiuso? "}
                        <span className="text-abnormal-acid">{lang === "en" ? "Let's see." : "Vediamo."}</span>
                    </p>
                </div>
            </section>

            {/* ═══════════ 05 · FEATURED TRAIL PHOTO ═══════════ */}
            {featured && (() => {
                const fName    = (lang === "en" && featured.name_en)    ? featured.name_en    : featured.name;
                const fSummary = ((lang === "en" && featured.summary_en) ? featured.summary_en : featured.summary) || (featured.rider_quotes?.[0]?.text ? `“${featured.rider_quotes[0].text}”` : "");
                return (
                    <section data-testid="featured-trail" className="relative bg-black border-t border-white/10">
                        <div className="grid md:grid-cols-2 items-stretch">
                            {/* PHOTO — dominant, clean */}
                            <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[70vh] overflow-hidden">
                                <img
                                    src={absolutizeUrl(featured.image_url)}
                                    alt={fName}
                                    className="absolute inset-0 w-full h-full object-cover"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                <div className="absolute top-6 left-6 bg-abnormal-acid text-black px-2 py-0.5 font-vt text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-1.5">
                                    <span className="w-1 h-1 rounded-full bg-black" />
                                    FEATURED
                                </div>
                                <div className="absolute top-6 right-6 flex items-center gap-1.5 font-vt text-[10px] tracking-[0.3em] uppercase text-abnormal-paper/85">
                                    <span className="w-1.5 h-1.5 rounded-full bg-abnormal-acid" />
                                    <span className="text-abnormal-acid">PIN</span>
                                    <span className="opacity-70">{ARGENTARIO_COORDS}</span>
                                </div>
                                <p className="absolute bottom-6 left-6 font-vt text-[10px] tracking-[0.3em] uppercase text-abnormal-paper/80">
                                    // {featured.difficulty} <span className="text-abnormal-acid">·</span> {featured.length_km} km <span className="text-abnormal-acid">·</span> D− {featured.elevation_m} m
                                </p>
                            </div>

                            {/* Content — clean, real data only */}
                            <div className="relative px-5 md:px-16 py-14 md:py-24 flex flex-col justify-center">
                                <p className="font-vt text-[11px] tracking-[0.35em] uppercase text-abnormal-acid mb-4">
                                    // N° 02 — {lang === "en" ? "Feature" : "In evidenza"}
                                </p>
                                <h2 className="font-display uppercase leading-[0.88] tracking-tighter text-abnormal-paper text-6xl md:text-8xl">
                                    {fName}<span className="text-abnormal-acid">.</span>
                                </h2>
                                <p className="mt-4 text-abnormal-paperDim font-display uppercase text-2xl leading-tight">{fSummary}</p>

                                <ul className="mt-8 grid grid-cols-3 gap-x-6 gap-y-2 max-w-md border-t border-white/10 pt-4">
                                    <li>
                                        <p className="font-vt text-[10px] tracking-[0.3em] uppercase text-abnormal-acid">// {lang === "en" ? "Length" : "Lunghezza"}</p>
                                        <p className="font-display text-2xl uppercase text-abnormal-paper leading-none mt-1">{featured.length_km}<span className="text-abnormal-acid text-sm ml-1">km</span></p>
                                    </li>
                                    <li>
                                        <p className="font-vt text-[10px] tracking-[0.3em] uppercase text-abnormal-acid">// {lang === "en" ? "Descent D−" : "Dislivello D−"}</p>
                                        <p className="font-display text-2xl uppercase text-abnormal-paper leading-none mt-1">{featured.elevation_m}<span className="text-abnormal-acid text-sm ml-1">m</span></p>
                                    </li>
                                    <li>
                                        <p className="font-vt text-[10px] tracking-[0.3em] uppercase text-abnormal-acid">// {lang === "en" ? "Level" : "Livello"}</p>
                                        <p className="font-display text-2xl uppercase text-abnormal-paper leading-none mt-1">{featured.difficulty}</p>
                                    </li>
                                </ul>

                                <div className="mt-8 flex flex-wrap items-center gap-4">
                                    <Link to={`/trail/${featured.slug}`} className="btn-primary">
                                        {t("home.featuredCta")} <ArrowUpRight size={14} />
                                    </Link>
                                    {featured.trailforks_url && (
                                        <a
                                            href={featured.trailforks_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid="featured-trailforks-link"
                                            className="group font-vt text-[11px] tracking-[0.35em] uppercase text-abnormal-acid inline-flex items-center gap-1.5 hover:underline underline-offset-4 decoration-1"
                                        >
                                            TRAILFORKS <ArrowUpRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                );
            })()}

            {/* ═══════════ 06 · DETOURS LINE ═══════════ */}
            <section className="bg-black border-t border-white/10">
                <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-8 md:py-10 text-center">
                    <p className="font-display uppercase text-2xl md:text-4xl tracking-tight text-abnormal-paper">
                        {lang === "en" ? "Detours? " : "Deviazioni? "}
                        <span className="text-abnormal-acid">{lang === "en" ? "Usually the good part." : "Di solito la parte bella."}</span>
                    </p>
                </div>
            </section>

            {/* ═══════════ 07 · PHOTO TRIPTYCH — the place ═══════════ */}
            <section data-testid="place-triptych" className="relative bg-black border-t border-white/10">
                <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12 md:py-16">
                    <div className="flex items-baseline justify-between border-b border-white/15 pb-3 mb-6">
                        <div className="flex items-baseline gap-3">
                            <span className="font-vt text-[11px] md:text-sm tracking-[0.35em] text-abnormal-acid">[ N° 03 ]</span>
                            <span className="w-4 h-px bg-abnormal-acid/60" />
                            <span className="font-vt text-[11px] md:text-sm tracking-[0.4em] uppercase text-abnormal-paper">
                                {lang === "en" ? "The place" : "Il posto"}
                            </span>
                        </div>
                        <span className="font-vt text-[10px] md:text-sm tracking-[0.2em] text-abnormal-acid">
                            {ARGENTARIO_COORDS}
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                        {trails.slice(4, 7).map((tr, i) => {
                            const name = (lang === "en" && tr.name_en) ? tr.name_en : tr.name;
                            return (
                                <Link
                                    key={tr.id}
                                    to={`/trail/${tr.slug}`}
                                    className="group relative block overflow-hidden aspect-[4/5] bg-zinc-900"
                                >
                                    <img
                                        src={absolutizeUrl(tr.image_url)}
                                        alt={name}
                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                                    <div className="absolute top-3 left-3 flex items-center gap-1.5 font-vt text-[10px] tracking-[0.3em] uppercase text-abnormal-paper/85">
                                        <span className="w-1 h-1 rounded-full bg-abnormal-acid" />
                                        <span>N° {String(i + 5).padStart(2, "0")}</span>
                                    </div>
                                    <div className="absolute bottom-3 left-3 right-3">
                                        <h3 className="font-display uppercase text-2xl md:text-3xl leading-[0.95] tracking-tight text-abnormal-paper">
                                            {name}
                                        </h3>
                                        <p className="mt-1 font-vt text-[10px] tracking-[0.3em] uppercase text-abnormal-paperDim/80">
                                            {tr.length_km} km <span className="text-abnormal-acid">·</span> {tr.elevation_m} m <span className="text-abnormal-acid">·</span> {tr.difficulty}
                                        </p>
                                        <span className="mt-2 block h-px w-0 bg-abnormal-acid group-hover:w-16 transition-all duration-500" />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ═══════════ 08 · OFF-FEED main slogan + brand seal band ═══════════ */}
            <section className="bg-black border-t border-white/10">
                <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-10 md:py-14 grid md:grid-cols-2 gap-8 items-center">
                    <div>
                        <p className="font-display uppercase text-3xl md:text-5xl tracking-tight text-abnormal-paper leading-none">
                            {lang === "en" ? "More dust." : "Più polvere."}<br />
                            <span className="text-abnormal-acid">{lang === "en" ? "Fewer algorithms." : "Meno algoritmi."}</span>
                        </p>
                        <p className="mt-4 font-vt text-[10px] md:text-xs tracking-[0.35em] uppercase text-abnormal-paperDim/60 flex items-center gap-2">
                            <span className="w-1 h-1 rounded-full bg-abnormal-acid" />
                            // {lang === "en" ? "look at the footer, curious one" : "guarda in fondo, curioso"}
                        </p>
                    </div>
                </div>
            </section>

            {/* ═══════════ 09 · ABNORMAL BRIDGE — the brand behind, volume slightly up ═══════════ */}
            <section data-testid="abnormal-bridge" className="border-t border-white/10 halftone">
                <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12 md:py-16 grid md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-8">
                        <p className="label-mono text-abnormal-acid flex items-center gap-2">
                            <span>[ N° 04 ]</span><span className="w-4 h-px bg-abnormal-acid/60" /><span>{lang === "en" ? "The brand behind" : "Il brand dietro"}</span>
                        </p>
                        <p className="mt-3 font-display uppercase leading-[0.88] tracking-tighter text-abnormal-paper text-5xl md:text-7xl">
                            {lang === "en" ? "Made by " : "Fatto da "}<span className="misprint">ABnormal</span><span className="text-abnormal-acid">.</span>
                        </p>
                        <p className="mt-4 max-w-xl text-abnormal-paperDim text-base md:text-lg leading-relaxed">
                            {lang === "en"
                                ? "Same world, volume up. Story, Murph and the official shop — all here."
                                : "Stesso mondo, volume alzato. Story, Murph e lo shop ufficiale — tutto qui."}
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/abnormal" data-testid="home-abnormal-link" className="btn-ghost">
                                ABNORMAL <ArrowRight size={14} className="text-abnormal-acid" />
                            </Link>
                            <Link to="/shop" data-testid="home-shop-link" className="label-mono text-abnormal-acid inline-flex items-center gap-1 self-center hover:underline underline-offset-4">
                                SHOP <ArrowRight size={11} />
                            </Link>
                        </div>
                    </div>
                    <div className="md:col-span-4 hidden md:flex justify-end">
                        <img src={ABNORMAL_ASSETS.brainMohawk} alt="Abnormal" width={200} loading="lazy" decoding="async" className="w-[180px] lg:w-[220px] h-auto -rotate-3 select-none pointer-events-none" />
                    </div>
                </div>
            </section>

            {/* ═══════════ 10 · NEWSLETTER ═══════════ */}
            <NewsletterForm />
        </div>
    );
}
